import React, { useState, useEffect } from 'react';
import {
  Lightbulb,
  Loader2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  X,
  FileText,
  Sparkles,
  Save,
  ArrowLeft
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import SupplementalPromptDisplay from './SupplementalPromptDisplay';
import TriviaLoader from './TriviaLoader';
import CollegeNotebook from '../CollegeNotebook/CollegeNotebook';

/**
 * Universal Essay Topic Development Component
 *
 * Works with ANY essay idea to generate personalized follow-up questions
 * that deepen the story and uncover emotional/narrative depth.
 *
 * @param {Object} essayIdea - Can be either:
 *   - A full essay idea object with `id` from database
 *   - A topic object from interview results (will need normalization)
 */
export default function EssayTopicDevelopment({ essayIdea, onComplete, onClose, onNavigateToOutlines }) {
  const { currentUser } = useFamilyContext();
  const [phase, setPhase] = useState('loading'); // 'loading' | 'questions' | 'processing' | 'complete' | 'error'
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [probeType, setProbeType] = useState('exploration');
  const [normalizedIdea, setNormalizedIdea] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isSavingOutline, setIsSavingOutline] = useState(false);
  const [savedOutlineId, setSavedOutlineId] = useState(null);
  const [supplementalContext, setSupplementalContext] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saving' | 'saved' | 'error'
  const autoSaveTimerRef = React.useRef(null);

  // Handle program verification
  const handleProgramVerification = async (programName, verificationData) => {
    if (!normalizedIdea?.id || !supplementalContext) return;

    try {
      // Update program verification in supplemental context
      const updatedContext = {
        ...supplementalContext,
        programs: supplementalContext.programs?.map(program =>
          program.name === programName
            ? { ...program, ...verificationData }
            : program
        )
      };

      setSupplementalContext(updatedContext);

      // Save to database
      const { error } = await supabase
        .from('college_journey_essay_ideas')
        .update({ supplemental_context: updatedContext })
        .eq('id', normalizedIdea.id);

      if (error) throw error;

      console.log('✅ Program verification saved:', programName);
    } catch (error) {
      console.error('❌ Failed to save program verification:', error);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  // Normalize essay idea structure (handle both database objects and interview results)
  useEffect(() => {
    if (essayIdea) {
      console.log('🔍 Normalizing essay idea:', essayIdea.title);
      console.log('🔍 Essay ID:', essayIdea.id || 'null (will be auto-created)');
      console.log('🔍 Essay Slot ID:', essayIdea.essay_slot_id || 'null (standalone idea)');
      console.log('🔍 Has development_responses?', !!essayIdea.development_responses);
      if (essayIdea.development_responses) {
        console.log('🔍 development_responses:', essayIdea.development_responses);
      }

      const normalized = {
        id: essayIdea.id || null,
        title: essayIdea.title,
        description: essayIdea.description || essayIdea.story || essayIdea.content,
        essay_angle: essayIdea.essay_angle || essayIdea.essayAngle,
        narrative_arc: essayIdea.narrative_arc || essayIdea.narrativeArc,
        source_quote: essayIdea.source_quote || essayIdea.sourceQuote,
        why_it_works: essayIdea.why_it_works || essayIdea.whyItWorks,
        differentiator: essayIdea.differentiator,
        potential_prompts: essayIdea.potential_prompts || essayIdea.potentialPrompts,
        interview_data: essayIdea.interview_data,
        development_count: essayIdea.development_count || 0,
        development_responses: essayIdea.development_responses, // IMPORTANT: Include this!
        // Supplemental essay fields
        essay_type: essayIdea.essay_type || 'personal',
        prompt: essayIdea.prompt,
        word_limit: essayIdea.word_limit,
        school_name: essayIdea.school_name,
        college_id: essayIdea.college_id,
        essay_slot_id: essayIdea.essay_slot_id || null, // CRITICAL: Preserve link to essay slot
        supplemental_context: essayIdea.supplemental_context
      };
      setNormalizedIdea(normalized);

      // Extract supplemental context if this is a supplemental essay
      if (normalized.essay_type === 'supplemental' && normalized.supplemental_context) {
        setSupplementalContext(normalized.supplemental_context);
      }
    }
  }, [essayIdea]);

  // Load questions on mount
  useEffect(() => {
    if (normalizedIdea) {
      loadDevelopmentQuestions();
    }
  }, [normalizedIdea]);

  // Load saved answer when question index changes OR when responses are loaded
  useEffect(() => {
    if (currentQuestion && responses[currentQuestion.id]) {
      setCurrentAnswer(responses[currentQuestion.id]);
      console.log(`📝 Loaded saved answer for question ${currentQuestion.id}`);
    } else {
      setCurrentAnswer('');
    }
  }, [currentQuestionIndex, questions, responses, currentQuestion]);

  // Auto-save responses (debounced)
  useEffect(() => {
    // Only auto-save if we have an essay ID (can't save to database without it)
    if (!normalizedIdea?.id || Object.keys(responses).length === 0) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer to save after 2 seconds of no changes
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');

        console.log('💾 Auto-saving responses:', responses);

        // Save to database (without generating analysis)
        const { error } = await supabase
          .from('college_journey_essay_ideas')
          .update({
            development_responses: responses,
            last_developed_at: new Date().toISOString()
          })
          .eq('id', normalizedIdea.id);

        if (error) throw error;

        setAutoSaveStatus('saved');
        console.log('✅ Auto-saved responses');
      } catch (error) {
        console.error('❌ Auto-save failed:', error);
        setAutoSaveStatus('error');
      }
    }, 2000);

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [responses, normalizedIdea?.id]);

  const loadDevelopmentQuestions = async () => {
    try {
      setPhase('loading');
      setError(null);

      console.log('📝 Generating development questions for:', normalizedIdea.title);

      // If no ID, we need to pass the full topic data instead
      const requestBody = normalizedIdea.id
        ? {
            action: 'generate_questions',
            essayIdeaId: normalizedIdea.id,
            userId: currentUser?.id,
            probeType: 'exploration',
            developmentStage: (normalizedIdea.development_count || 0) + 1
          }
        : {
            action: 'generate_questions',
            essayTopic: normalizedIdea, // Pass full topic for non-persisted ideas
            userId: currentUser?.id,
            probeType: 'exploration',
            developmentStage: 1
          };

      const response = await fetch('/api/essay-ideas/develop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      // First get the response text
      const responseText = await response.text();

      if (!response.ok) {
        // Try to parse error as JSON
        let errorMessage = 'Failed to generate questions';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          // Response is not JSON (likely HTML error page)
          console.error('❌ Non-JSON error response:', responseText.substring(0, 500));
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Parse successful response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Failed to parse response:', responseText.substring(0, 500));
        throw new Error('Invalid response from server');
      }

      if (!data.questions || data.questions.length === 0) {
        throw new Error('No questions generated');
      }

      console.log(`✓ Loaded ${data.questions.length} questions (source: ${data.source})`);

      // If essay was auto-created, update normalizedIdea with the new ID
      if (data.autoCreated && data.essayIdea?.id) {
        console.log('✅ Essay auto-created with ID:', data.essayIdea.id);
        setNormalizedIdea(prev => ({
          ...prev,
          id: data.essayIdea.id,
          ...data.essayIdea // Include any other fields from the response
        }));
      }

      setQuestions(data.questions);
      setProbeType(data.probeType);

      // For supplemental essays, track current phase
      if (data.currentPhase) {
        setCurrentPhase(data.currentPhase);
      }

      // Load existing responses if they exist (resume functionality)
      if (normalizedIdea?.development_responses) {
        console.log('📋 Found existing responses, loading for resume...');

        // Handle both formats:
        // 1. New format: flat object { question_id: "answer" }
        // 2. Old format: stage-based { stage1: { timestamp, responses: {...} } }
        let existingResponses = {};

        if (typeof normalizedIdea.development_responses === 'object') {
          // Check if it's the new flat format (has string values directly)
          const hasDirectValues = Object.values(normalizedIdea.development_responses).some(
            val => typeof val === 'string'
          );

          if (hasDirectValues) {
            // New flat format
            existingResponses = normalizedIdea.development_responses;
          } else {
            // Old stage-based format
            Object.values(normalizedIdea.development_responses).forEach(stage => {
              if (stage.responses) {
                Object.assign(existingResponses, stage.responses);
              }
            });
          }
        }

        if (Object.keys(existingResponses).length > 0) {
          console.log('✅ Loaded responses:', existingResponses);
          setResponses(existingResponses);

          // Find first unanswered question
          const firstUnansweredIndex = data.questions.findIndex(
            q => !existingResponses[q.id] || !existingResponses[q.id].trim()
          );

          if (firstUnansweredIndex !== -1) {
            setCurrentQuestionIndex(firstUnansweredIndex);
            console.log(`📍 Resuming at question ${firstUnansweredIndex + 1}`);
            console.log(`📍 First question ID: ${data.questions[firstUnansweredIndex].id}`);
          } else {
            // All questions answered - start at beginning for review
            setCurrentQuestionIndex(0);
            console.log('✅ All questions answered - starting at beginning for review');
            console.log(`📍 First question ID: ${data.questions[0].id}`);
          }
        } else {
          console.log('⚠️ No existing responses found');
        }
      }

      setPhase('questions');

    } catch (err) {
      console.error('❌ Failed to load questions:', err);
      setError(err.message);
      setPhase('error');
    }
  };

  const submitAnswer = () => {
    if (!currentAnswer.trim()) {
      return; // Don't submit empty answers
    }

    // Proceed with submission
    const newResponses = {
      ...responses,
      [currentQuestion.id]: currentAnswer.trim()
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setCurrentAnswer('');

      // Update phase if moving to a new phase
      const nextQuestion = questions[nextIndex];
      if (nextQuestion?.phase && nextQuestion.phase !== currentPhase) {
        setCurrentPhase(nextQuestion.phase);
      }
    } else {
      saveResponses(newResponses);
    }
  };

  const goBack = () => {
    if (currentQuestionIndex > 0) {
      // Save current answer before going back
      if (currentAnswer.trim()) {
        setResponses({
          ...responses,
          [currentQuestion.id]: currentAnswer.trim()
        });
      }

      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);

      // Load previous answer
      const prevQuestion = questions[prevIndex];
      setCurrentAnswer(responses[prevQuestion.id] || '');

      // Update phase if going back to different phase
      if (prevQuestion?.phase && prevQuestion.phase !== currentPhase) {
        setCurrentPhase(prevQuestion.phase);
      }
    }
  };

  const skipQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setCurrentAnswer('');

      // Update phase if moving to a new phase
      const nextQuestion = questions[nextIndex];
      if (nextQuestion?.phase && nextQuestion.phase !== currentPhase) {
        setCurrentPhase(nextQuestion.phase);
      }
    } else {
      // Last question - save what we have
      saveResponses(responses);
    }
  };

  const saveResponses = async (finalResponses) => {
    try {
      setPhase('processing');
      setIsSubmitting(true);

      console.log('💾 Saving development responses...');

      // For topics without IDs, we can't save to database yet
      // Just complete the flow and return the responses to parent
      if (!normalizedIdea.id) {
        console.log('⚠️ No essay ID - skipping database save');
        setPhase('complete');

        if (onComplete) {
          onComplete({
            essayTopic: normalizedIdea,
            responses: finalResponses,
            developmentCount: 1
          });
        }
        return;
      }

      const response = await fetch('/api/essay-ideas/develop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_responses',
          essayIdeaId: normalizedIdea.id,
          responses: finalResponses
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save responses');
      }

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Failed to parse save response:', responseText.substring(0, 500));
        throw new Error('Invalid response from server');
      }

      console.log(`✓ Saved responses (development session #${data.developmentCount})`);

      // Store the analysis for display
      if (data.analysis) {
        setAnalysis(data.analysis);
        console.log('✓ Received analysis:', data.analysis);

        // Automatically save as outline (if user ID is available)
        if (currentUser?.id) {
          console.log('🔄 Auto-saving as outline...');
          const outlineId = await autoSaveOutline(data.analysis);
          if (outlineId) {
            setSavedOutlineId(outlineId);

            // Notify parent component first
            if (onComplete) {
              onComplete({
                essayIdeaId: normalizedIdea.id,
                responses: finalResponses,
                developmentCount: data.developmentCount,
                analysis: data.analysis,
                outlineId
              });
            }

            // Auto-navigate to outlines after a brief delay
            setTimeout(() => {
              console.log('🔄 Auto-navigating to outlines view...');
              if (onNavigateToOutlines) {
                onNavigateToOutlines();
              }
              if (onClose) {
                onClose();
              }
            }, 1500); // 1.5 second delay so user sees the success message

            return; // Exit early to skip duplicate onComplete and setPhase
          }
        }
      }

      setPhase('complete');

      // Notify parent component (only if outline didn't auto-save)
      if (onComplete) {
        onComplete({
          essayIdeaId: normalizedIdea.id,
          responses: finalResponses,
          developmentCount: data.developmentCount,
          analysis: data.analysis
        });
      }

    } catch (err) {
      console.error('❌ Failed to save responses:', err);
      setError(err.message);
      setPhase('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      submitAnswer();
    }
  };

  // Auto-save outline (called automatically after analysis)
  const autoSaveOutline = async (analysisData) => {
    if (!analysisData || !currentUser?.id) {
      console.error('Cannot save outline - missing analysis or user');
      return null;
    }

    try {
      const outlineData = {
        user_id: currentUser.id,
        essay_idea_id: normalizedIdea?.id || null,
        title: normalizedIdea?.title || 'Untitled Outline',
        strategic_angle: analysisData.strategic_angle,
        outline_data: analysisData.essay_outline,
        outline_type: 'guided',
        source: 'topic_development',
        key_moments: analysisData.key_moments?.map(m => m.moment) || [],
        themes: analysisData.key_themes || [],
        status: 'ready'
      };

      console.log('📤 Saving outline to database:', outlineData);

      const { data, error } = await supabase
        .from('college_journey_essay_outlines')
        .insert([outlineData])
        .select()
        .single();

      if (error) {
        console.error('❌ Database error saving outline:', error);
        throw error;
      }

      console.log('✅ Successfully saved outline:', data.id);
      return data.id;

    } catch (err) {
      console.error('❌ Failed to auto-save outline:', err);
      console.error('Error details:', err.message, err.details, err.hint);
      return null;
    }
  };

  // Manual save outline (button click)
  const saveAsOutline = async () => {
    if (!analysis || !currentUser?.id) {
      console.error('Cannot save outline - missing analysis or user');
      return;
    }

    try {
      setIsSavingOutline(true);
      const outlineId = await autoSaveOutline(analysis);

      if (outlineId) {
        // Show success and close
        if (onComplete) {
          onComplete({
            ...normalizedIdea,
            analysis,
            outlineId
          });
        }

        if (onClose) onClose();
      } else {
        alert('Failed to save outline. Please check the console for details.');
      }

    } catch (err) {
      console.error('Failed to save outline:', err);
      alert('Failed to save outline. Please try again.');
    } finally {
      setIsSavingOutline(false);
    }
  };

  // Loading state with trivia
  if (phase === 'loading') {
    return (
      <div className="fixed inset-0 z-50 bg-stone-50">
        <TriviaLoader
          college={null} // TODO: Pass college object when available from slot/idea
          schoolName={normalizedIdea?.school_name || 'this college'}
          message="Generating personalized questions..."
        />
      </div>
    );
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="fixed inset-0 z-50 bg-stone-50 overflow-y-auto">
        <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
          <div className="flex items-start mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Error</h3>
          <p className="text-red-700 mb-6">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={loadDevelopmentQuestions}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Questions phase
  if (phase === 'questions' && currentQuestion) {
    // Determine if we should show strategic context based on current question phase
    const shouldShowStrategicContext = currentQuestion?.show_strategic_context ||
                                       currentPhase === 'research_connection' ||
                                       currentPhase === 'strategic_positioning';

    const contextType = currentQuestion?.context_type ||
                       (currentPhase === 'strategic_positioning' ? 'full_uvp' : 'reference_only');

    return (
      <div className="fixed inset-0 z-50 bg-stone-50 overflow-y-auto">
        {/* College Notebook - only show for supplemental essays with college_id */}
        {normalizedIdea?.essay_type === 'supplemental' && normalizedIdea?.college_id && (
          <CollegeNotebook
            collegeId={normalizedIdea.college_id}
            essaySlotId={normalizedIdea.essay_slot_id}
          />
        )}

        <div className="min-h-screen">
          <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sm:p-8 mb-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                      title="Go back"
                    >
                      <ArrowLeft className="w-5 h-5 text-stone-600" />
                    </button>
                  )}
                  <h3 className="text-2xl font-bold text-slate-900">Develop Your Topic</h3>
                </div>
                <div className="flex items-center gap-3 ml-14">
                  <p className="text-slate-600 text-sm">{normalizedIdea?.title}</p>
                  {autoSaveStatus === 'saving' && (
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Saving...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && normalizedIdea?.id && (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Saved
                    </span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Save failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Supplemental Prompt Display - Only for supplemental essays */}
          {normalizedIdea?.essay_type === 'supplemental' && normalizedIdea.prompt && (
            <div className="mb-6">
              <SupplementalPromptDisplay
                prompt={normalizedIdea.prompt}
                wordLimit={normalizedIdea.word_limit}
                schoolName={normalizedIdea.school_name}
                supplementalContext={supplementalContext}
                showStrategicContext={shouldShowStrategicContext}
                contextType={contextType}
                currentPhase={currentPhase}
                onProgramVerification={handleProgramVerification}
              />
            </div>
          )}

          {/* Main Content Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sm:p-8">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <span className="text-sm text-slate-600">{Math.round(progress)}% complete</span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="mb-6">
            <label className="block text-lg font-semibold text-slate-900 mb-3">
              {currentQuestion.question}
            </label>

            {/* Guidance Callout - Always visible */}
            {currentQuestion.placeholder && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">Think about:</h4>
                    <p className="text-sm text-blue-800 leading-relaxed whitespace-pre-line">
                      {currentQuestion.placeholder}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Start typing your response here..."
              className="w-full p-4 border-2 border-stone-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors min-h-[150px]"
              autoFocus
            />

            <p className="text-sm text-slate-500 mt-2">
              Press Ctrl+Enter to continue, or use the buttons below
            </p>
          </div>

          {/* Answered count */}
          <div className="mb-6">
            <p className="text-sm text-slate-600">
              {Object.values(responses).filter(r => typeof r === 'string' && r.trim().length > 0).length} of {questions.length} questions answered
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Subtle reminder - only shows if there's guidance and user has typed something */}
            {currentQuestion.placeholder && currentAnswer.trim() && (
              <div className="flex items-center justify-end gap-2 text-xs text-blue-600 animate-fade-in">
                <span>💡</span>
                <span className="font-medium">Referenced the guidance above?</span>
              </div>
            )}

            <div className="flex gap-3">
              {currentQuestionIndex > 0 && (
                <button
                  onClick={goBack}
                  className="px-6 py-3 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors font-medium flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <button
                onClick={skipQuestion}
                className="px-6 py-3 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors font-medium"
              >
                Skip
              </button>
              <button
                onClick={submitAnswer}
                disabled={!currentAnswer.trim()}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {currentQuestionIndex < questions.length - 1 ? (
                  <>
                    Next Question
                    <ChevronRight className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Complete Development
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
    );
  }

  // Processing state
  if (phase === 'processing') {
    return (
      <div className="fixed inset-0 z-50 bg-stone-50 overflow-y-auto">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Saving Your Responses</h3>
              <p className="text-slate-600">
                Adding depth to your essay topic...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Complete state
  if (phase === 'complete') {
    return (
      <div className="fixed inset-0 z-50 bg-stone-50 overflow-y-auto">
        {/* College Notebook - only show for supplemental essays with college_id */}
        {normalizedIdea?.essay_type === 'supplemental' && normalizedIdea?.college_id && (
          <CollegeNotebook
            collegeId={normalizedIdea.college_id}
            essaySlotId={normalizedIdea.essay_slot_id}
          />
        )}

        <div className="min-h-screen">
          <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Essay Development Complete!</h3>
            <p className="text-slate-600">
              We've analyzed your responses and created a strategic roadmap for your essay.
            </p>
          </div>

          {analysis ? (
            <div className="space-y-6">
              {/* Strategic Angle */}
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border-2 border-violet-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-violet-900 uppercase tracking-wide mb-2">Strategic Angle</h4>
                    <p className="text-slate-900 text-lg leading-relaxed">{analysis.strategic_angle}</p>
                  </div>
                </div>
              </div>

              {/* Key Themes & Emotional Core */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-teal-900 uppercase tracking-wide mb-3">Key Themes</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.key_themes?.map((theme, i) => (
                      <span key={i} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-rose-50 border border-rose-200 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-rose-900 uppercase tracking-wide mb-3">Emotional Core</h4>
                  <p className="text-slate-700 text-sm leading-relaxed">{analysis.emotional_core}</p>
                </div>
              </div>

              {/* Essay Outline */}
              {analysis.essay_outline && (
                <div className="bg-white border-2 border-slate-300 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Essay Outline
                  </h4>
                  <div className="space-y-4">
                    {Object.entries(analysis.essay_outline).map(([key, value]) => (
                      <div key={key} className="border-l-4 border-indigo-500 pl-4">
                        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-1">
                          {key.replace(/_/g, ' ')}
                        </div>
                        {typeof value === 'object' && value !== null ? (
                          // Handle nested objects (like {title, narrative, description})
                          <div className="text-slate-700 text-sm leading-relaxed space-y-2">
                            {Object.entries(value).map(([nestedKey, nestedValue]) => (
                              <div key={nestedKey}>
                                <span className="font-semibold">{nestedKey.replace(/_/g, ' ')}:</span>{' '}
                                {typeof nestedValue === 'string' ? nestedValue : JSON.stringify(nestedValue)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-700 text-sm leading-relaxed">{value}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Moments */}
              {analysis.key_moments && analysis.key_moments.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-4">Key Moments to Highlight</h4>
                  <div className="space-y-4">
                    {analysis.key_moments.map((moment, i) => (
                      <div key={i} className="bg-white rounded-lg p-4 border border-amber-200">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 mb-1">{moment.moment}</p>
                            <p className="text-sm text-slate-600 mb-2">{moment.why_important}</p>
                            <p className="text-xs text-amber-700 italic">{moment.details_to_include}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Narrative Strengths & Areas to Develop */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-emerald-900 uppercase tracking-wide mb-3">Narrative Strengths</h4>
                  <ul className="space-y-2">
                    {analysis.narrative_strengths?.map((strength, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wide mb-3">Areas to Develop</h4>
                  <ul className="space-y-2">
                    {analysis.areas_to_develop?.map((area, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <ChevronRight className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Authenticity Notes */}
              {analysis.authenticity_notes && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                  <h4 className="text-sm font-bold text-purple-900 uppercase tracking-wide mb-3">What Makes This Yours</h4>
                  <p className="text-slate-700 leading-relaxed">{analysis.authenticity_notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-600 mb-4">
                Your responses have been saved! You're ready to begin drafting your essay.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            {analysis && !savedOutlineId && (
              <button
                onClick={saveAsOutline}
                disabled={isSavingOutline}
                className="flex-1 px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingOutline ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save as Outline
                  </>
                )}
              </button>
            )}
            {savedOutlineId && (
              <button
                onClick={() => {
                  if (onNavigateToOutlines) {
                    onNavigateToOutlines();
                  }
                  if (onClose) onClose();
                }}
                className="flex-1 px-6 py-3 bg-emerald-50 border-2 border-emerald-500 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                Outline Saved! Go to Outlines →
              </button>
            )}
            <button
              onClick={() => {
                // TODO: Navigate to essay drafting with pre-loaded analysis
                console.log('Begin drafting with:', { idea: normalizedIdea, analysis });
                if (onClose) onClose();
              }}
              className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Begin Drafting
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-6 py-3 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
            )}
          </div>
        </div>
        </div>
        </div>
      </div>
    );
  }

  return null;
}
